import React, { forwardRef } from 'react';
import { Add, type IconComponent } from '@/components/icons';

interface PlaceholderBlockProps extends React.HTMLAttributes<HTMLDivElement> {
    type?: string;
    label: string;
    description: React.ReactNode;
    disabled?: boolean;
    /** Names the block being stood in for. Without it, the neutral grey square. */
    icon?: IconComponent;
}

const PlaceholderBlock = forwardRef<HTMLDivElement, PlaceholderBlockProps>(
    ({ onClick, label, description, className, disabled = false, icon: Icon, ...props }, ref) => {
        return (
            <div
                ref={ref}
                onClick={disabled ? undefined : onClick}
                aria-disabled={disabled}
                className={`mb-6 flex items-center justify-between rounded-lg border-2 border-dashed border-border-strong bg-card p-6 transition-all ${
                    disabled
                        ? 'cursor-default opacity-70'
                        : 'group cursor-pointer hover:border-blue-500 hover:bg-blue-50'
                } ${className || ''}`}
                {...props}
            >
                <div className="flex items-center gap-4">
                    <div className="flex size-12 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-blue-100 group-hover:text-blue-500">
                        {Icon ? (
                            <Icon className="size-6" />
                        ) : (
                            <div className="size-6 rounded-sm bg-current opacity-20" />
                        )}
                    </div>
                    <div>
                        <h3 className="font-medium text-foreground group-hover:text-blue-700">
                            {label}
                        </h3>
                        <p className="text-sm text-muted-foreground">{description}</p>
                    </div>
                </div>
                <div className="flex size-8 items-center justify-center rounded-full border border-border-strong text-muted-foreground transition-all group-hover:border-blue-500 group-hover:text-blue-500">
                    <Add className="size-4" />
                </div>
            </div>
        );
    },
);

PlaceholderBlock.displayName = 'PlaceholderBlock';

export default PlaceholderBlock;
